import CloseIcon from "@mui/icons-material/Close"
import {
    Divider,
    Box,
    IconButton,
    Stack,
    Typography,
    SwipeableDrawer,
    useTheme,
    ListItem,
    List,
    Link,
    Alert,
    AlertTitle,
} from "@mui/material"
import { ReactElement } from "react"
import { useNavigate } from "react-router-dom"

import { SIDE_BAR_NON_MOBILE_WIDTH } from "../../constants/generalConstants"
import { useAppDispatch } from "../../hooks/useDispatch"
import { useGetState } from "../../hooks/useGetState"
import { useGetUserData } from "../../hooks/useGetUserData"
import { useScreenSize } from "../../hooks/useScreenSize"
import { useSupportPortalUrl } from "../../hooks/useSupportPortalUrl"
import {
    DARK,
    LIGHT,
    getNavBarExpand,
    getShowPreferences,
    getSideBar,
    getTheme,
    setNavBarExpand,
    setShowMFA,
    setShowPreferences,
    setSideBar,
    setTheme,
} from "../../redux/preferencesSlice"
import {
    PRIVACY_POLICY,
    TERMS_OF_USE,
    COOKIE_POLICY,
} from "../../router/routes"
import { container } from "../../themes/baseTheme"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import AppButton from "../buttons/appButton"
import AppSwitch from "../buttons/switch/appSwitch"
import AppAlert from "../dataDisplay/alerts/appAlert"
import AppTooltip from "../helper/appTooltip"
import DisplayIf from "../wrappers/displayIf"

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
// eslint-disable-next-line no-undef
export const CONFIG = GLOBAL_CONFIG

/**
 * Side-drawer for user settings. Allows users to customise their experience
 * on the DoC web portal. In order to make this component available to
 * entire application, it should be placed in the `App` component.
 *
 *
 * @example
 * <App>
 *     <AppContent />
 *     <PreferencesSidebar />
 * </App>
 */

// About the ENA
const ABOUT_ENA = "https://www.energynetworks.org/about/"
const SUPPORT_MAILBOX = "ena-connect-direct@baringa.com"

const PreferencesSidebar = (): ReactElement => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const open = useGetState(getShowPreferences)
    const uiTheme = useGetState(getTheme)
    const navbarExpand = useGetState(getNavBarExpand)
    const sideBar = useGetState(getSideBar)
    const { isMob } = useScreenSize()
    const {
        displayGroups,
        firstName,
        lastName,
        isMfaRequired: mfaRequired,
        showUnapprovedWarning,
        isSignedIn,
    } = useGetUserData()

    const handleClose = (): void => {
        dispatch(setShowPreferences(false))
    }

    const navigate = useNavigate()

    const navigateAndClose = (route: string): void => {
        navigate(route)
        handleClose()
    }

    const { supportPortalUrl, supportPortalMessage } = useSupportPortalUrl()

    return (
        <SwipeableDrawer
            anchor="right"
            open={open}
            onClose={handleClose}
            onOpen={() => ({})}
            ModalProps={{
                container: container(),
            }}
        >
            <Box
                padding={4}
                width={isMob ? "100vw" : SIDE_BAR_NON_MOBILE_WIDTH}
                sx={{
                    backgroundColor: "background.contentBackground",
                    overflowY: "auto",
                    height: "100%",
                }}
            >
                <Stack spacing={GRID_SPACING} direction="column">
                    <Box>
                        <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="space-between"
                        >
                            <Typography
                                fontWeight="bold"
                                gutterBottom
                                variant="h5"
                                component="div"
                            >
                                <Box
                                    color={theme.palette.primary.main}
                                    display="inline"
                                >
                                    Welcome
                                </Box>{" "}
                                {firstName
                                    ? `${firstName} ${lastName}`
                                    : "to Connect Direct"}
                            </Typography>

                            <IconButton
                                size="small"
                                aria-label="close"
                                color="inherit"
                                onClick={handleClose}
                                sx={{ mt: "-10px" }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Typography variant="body1">
                            Use this panel to customise your experience on
                            Connect Direct. Preferences listed here will be
                            saved and applied to future browsing sessions
                        </Typography>
                    </Box>
                    <Divider />
                    <DisplayIf condition={isSignedIn}>
                        <>
                            <Box>
                                <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    gutterBottom
                                >
                                    My Roles
                                </Typography>
                                <Typography variant="body1">
                                    Your roles determine the actions you can
                                    perform on Connect Direct. You have the
                                    following roles:
                                </Typography>
                                <List sx={{ listStyleType: "disc", pl: 4 }}>
                                    {displayGroups.map((role) => (
                                        <ListItem
                                            key={role}
                                            sx={{ display: "list-item" }}
                                        >
                                            <AppTooltip title={role}>
                                                <Typography variant="body1">
                                                    {role}
                                                </Typography>
                                            </AppTooltip>
                                        </ListItem>
                                    ))}
                                </List>
                                <DisplayIf condition={mfaRequired}>
                                    <AppTooltip title="Click to set up MFA">
                                        <Alert
                                            severity="info"
                                            onClick={() => {
                                                handleClose()
                                                dispatch(setShowMFA(true))
                                            }}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <AlertTitle>
                                                You need to set up MFA
                                            </AlertTitle>
                                            You have not configured multi-factor
                                            authentication (MFA). You will not
                                            be able to access any privileged
                                            information until this has been
                                            configured.{" "}
                                            <strong>
                                                Click this alert box to set up
                                                MFA
                                            </strong>
                                        </Alert>
                                    </AppTooltip>
                                </DisplayIf>
                                <DisplayIf condition={showUnapprovedWarning}>
                                    <AppTooltip title="Your account needs to be approved">
                                        <AppAlert severity="info">
                                            <AlertTitle>
                                                Your account is unapproved
                                            </AlertTitle>
                                            Please ask your company Connect
                                            Direct admin to approve your
                                            account. You will not be able to
                                            access any privileged information
                                            until your account has been approved
                                        </AppAlert>
                                    </AppTooltip>
                                </DisplayIf>
                            </Box>
                            <Divider />
                        </>
                    </DisplayIf>
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        paddingRight="50px"
                    >
                        <Typography variant="h6" fontWeight="bold">
                            Dark Theme
                        </Typography>

                        <AppSwitch
                            checked={uiTheme === DARK}
                            onChange={() =>
                                dispatch(
                                    setTheme(uiTheme === DARK ? LIGHT : DARK)
                                )
                            }
                        />
                    </Box>
                    <Divider />
                    <DisplayIf condition={!isMob}>
                        <>
                            <Box
                                display="flex"
                                flexDirection="row"
                                justifyContent="space-between"
                                paddingRight="50px"
                            >
                                <Typography variant="h6" fontWeight="bold">
                                    Side Navigation Bar
                                </Typography>
                                <AppSwitch
                                    checked={sideBar}
                                    onChange={(e) =>
                                        dispatch(setSideBar(e.target.checked))
                                    }
                                />
                            </Box>
                            <Divider />
                        </>
                    </DisplayIf>

                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        paddingRight="50px"
                    >
                        <Typography variant="h6" fontWeight="bold">
                            Side Bar Expanded
                        </Typography>
                        <AppSwitch
                            disabled={!sideBar}
                            checked={navbarExpand}
                            onChange={(e) =>
                                dispatch(setNavBarExpand(e.target.checked))
                            }
                        />
                    </Box>
                    <Divider />
                    <Box>
                        <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                gutterBottom
                            >
                                Need Help?
                            </Typography>
                        </Box>
                        <Typography variant="body1">
                            If you have any questions or need help with
                            anything, please raise a ticket with our support
                            team via the{" "}
                            <Link href={supportPortalUrl} target="_blank">
                                Connect Direct Support Portal
                            </Link>
                            . {supportPortalMessage}
                            If you are unable to access the support portal, you
                            can contact us via email at{" "}
                            <Link
                                href={`mailto:${SUPPORT_MAILBOX}`}
                                target="_blank"
                            >
                                {SUPPORT_MAILBOX}
                            </Link>
                            .
                        </Typography>
                    </Box>
                    <Divider />
                    <Typography
                        paragraph
                        textAlign="center"
                        variant="body1"
                        gutterBottom
                        pt={1}
                    >
                        <AppButton
                            onClick={() => navigateAndClose(PRIVACY_POLICY)}
                        >
                            Privacy Policy
                        </AppButton>
                        {" | "}
                        <AppButton
                            onClick={() => navigateAndClose(TERMS_OF_USE)}
                        >
                            Terms of Use
                        </AppButton>
                        {" | "}
                        <AppButton
                            onClick={() => navigateAndClose(COOKIE_POLICY)}
                        >
                            Cookie Policy
                        </AppButton>
                    </Typography>
                    <Typography
                        variant="body1"
                        color={theme.palette.text.secondary}
                        fontWeight={400}
                        align="center"
                    >
                        This website is operated by Energy Networks Association
                        Limited. We are registered in England and Wales under
                        company number 04832301 and have our registered office
                        at 30 Coleman Street London EC2R 5AL. Our VAT number is
                        GB 830 7271 43. Click here to learn more{" "}
                        <Link href={ABOUT_ENA} target="_blank">
                            about ENA
                        </Link>
                        .
                    </Typography>
                </Stack>
            </Box>
        </SwipeableDrawer>
    )
}

export default PreferencesSidebar
